import Phaser from "phaser";
import { DIFFICULTIES, SCENARIOS } from "../config/campaign";
import { TOWERS } from "../config/towers";
import type { LevelResult } from "../domain/types";
import { loadSave, writeSave } from "../systems/save";
import { AudioManager } from "../systems/audio";
import { defenseAnalytics } from "../systems/analytics";
import { applyLevelResult, type ProgressionAward } from "../systems/progression";
import { recordDefenseResult } from "../systems/platform-integration";
import {
  addButton,
  addCastleBackdrop,
  addLittleCastleFooter,
  addRoyalCelebration,
  COLORS,
  title,
} from "./shared";
export class ResultsScene extends Phaser.Scene {
  private result!: LevelResult;
  constructor() {
    super("Results");
  }
  init(data: { result: LevelResult }): void {
    this.result = data.result;
  }
  create(): void {
    addCastleBackdrop(this);
    const progression = this.persist();
    const audio = new AudioManager(loadSave().settings);
    audio.bindUnlock(this);
    audio.cue(this.result.victory ? "victory" : "defeat");
    audio.playMusic(this.result.victory ? "victory" : "defeat");
    defenseAnalytics.track("results_viewed", {
      victory: this.result.victory,
      mapId: this.result.mapId,
      scenarioId: this.result.scenarioId,
      difficulty: this.result.difficulty,
      wavesCompleted: this.result.wavesCompleted,
    });
    this.cameras.main.setBackgroundColor(COLORS.ink);
    title(
      this,
      this.result.victory ? "The Keep Stands!" : "Lion Gate Has Fallen",
      this.result.victory
        ? "The royal road is secure. The kingdom sings of your cunning maze."
        : "Regroup, rebuild, and make the runners earn every stone.",
    );
    const x = this.scale.width / 2;
    this.add
      .text(
        x,
        275,
        `Waves ${this.result.wavesCompleted}/${DIFFICULTIES[this.result.difficulty].waveCount}\nLives ${this.result.livesRemaining}\nScore ${this.result.score.toLocaleString()}`,
        {
          fontFamily: "Georgia, serif",
          fontSize: "25px",
          align: "center",
          color: "#fff3cf",
          lineSpacing: 9,
        },
      )
      .setOrigin(0.5);
    const medalCount = this.result.victory
      ? (progression.save.progress.scenarioStars?.[progression.scenarioId] ?? 0)
      : 0;
    this.add
      .text(x, 345, medalCount > 0 ? "★".repeat(medalCount) : "NO ROYAL MEDAL", {
        fontFamily: "Georgia, serif",
        fontSize: medalCount > 0 ? "34px" : "16px",
        color: medalCount > 0 ? "#f3c656" : "#c78278",
        letterSpacing: 8,
      })
      .setOrigin(0.5);
    const unlockNames = [
      ...progression.newlyUnlockedScenarioIds.map(
        (id) => SCENARIOS.find((scenario) => scenario.id === id)?.displayName ?? id,
      ),
      ...progression.newlyUnlockedTowerIds.map((id) => TOWERS[id]?.displayName ?? id),
    ];
    this.add
      .text(
        x,
        384,
        this.result.victory
          ? progression.firstCompletion
            ? `Rewards: +${progression.starsAwarded} ★  +${progression.royalSealsAwarded} royal seals${
                unlockNames.length > 0 ? `\nUnlocked: ${unlockNames.join(", ")}` : ""
              }`
            : "Scenario already completed — best result and royal record updated."
          : "No campaign rewards awarded. The royal record still remembers this attempt.",
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: "14px",
          color: this.result.victory ? "#dfc889" : "#c78278",
          align: "center",
          lineSpacing: 5,
          wordWrap: { width: Math.min(680, this.scale.width - 36) },
        },
      )
      .setOrigin(0.5);
    addRoyalCelebration(this, this.result.victory);
    addLittleCastleFooter(this, this.result.victory ? "Victory Chronicle" : "Royal War Record");
    addButton(this, x, 455, 230, "Defend Again", () => this.scene.start("Game"), true);
    addButton(this, x, 515, 230, "Campaign Map", () => this.scene.start("LevelSelect"));
  }
  private persist(): ProgressionAward {
    const award = applyLevelResult(loadSave(), this.result);
    writeSave(award.save);
    void recordDefenseResult(this.result).catch((error: unknown) => {
      console.warn("[Defense platform] result remains queued in the local save", error);
    });
    return award;
  }
}
